// Routing skeleton and the guard decision (U3). The route table is wired here;
// the guard is a pure function so the acceptance rule "direct-URL bypass is
// impossible" is unit-testable without a browser.
//
// No finished screens are built. No section of SPEC-PARTNER-U1-U3.md carries
// [APPROVED], so per BUILD-ORDER only plumbing, scaffold, routing, session and
// authz logic, and harnesses exist now; each route resolves to a provisional
// placeholder until its spec section is approved.

import type { SessionState } from './session/session'
import type { PartnerAuthorization } from './authz/partner'

/** Every route the shell resolves (spec 1). */
export const ROUTES = {
  home: '/',
  access: '/access',
  pending: '/pending',
  signedOut: '/signed-out',
  unavailable: '/unavailable',
} as const

/** What the shell renders for a request. A view, not a URL. */
export type View =
  | 'loading'
  | 'home'
  | 'access'
  | 'pending'
  | 'signed-out'
  | 'unavailable'
  | 'error'
  | 'not-found'

/** Routes the guard does not protect (spec 4.1). */
const UNGUARDED = new Set<string>([
  ROUTES.access,
  ROUTES.pending,
  ROUTES.signedOut,
  ROUTES.unavailable,
])

function isKnownRoute(path: string): boolean {
  return (Object.values(ROUTES) as string[]).includes(path)
}

/**
 * Resolve the view for a requested path given the current gate state.
 *
 * The guard is the whole point: a guarded path (today only `/`) never resolves
 * to its content unless the session is verified AND authorization is
 * authorized. Every other combination is sent to the state screen that matches,
 * so typing `/` directly cannot bypass the gate in any non-authorized state.
 *
 * Unguarded state routes render regardless, so a signed-out visitor can read
 * the signed-out screen. Unknown paths are not-found.
 */
export function resolveView(
  path: string,
  session: SessionState,
  authz: PartnerAuthorization,
): View {
  if (!isKnownRoute(path)) return 'not-found'
  if (UNGUARDED.has(path)) return viewForUnguarded(path)

  // Guarded from here (path === ROUTES.home).
  if (session.status === 'not_configured') return 'unavailable'
  if (session.status === 'signed_out') return 'signed-out'
  // session verified: authorization decides.
  switch (authz.status) {
    case 'loading': return 'loading'
    case 'authorized': return 'home'
    case 'pending': return 'pending'
    case 'none': return 'access'
    case 'error': return 'error'
  }
}

function viewForUnguarded(path: string): View {
  switch (path) {
    case ROUTES.access: return 'access'
    case ROUTES.pending: return 'pending'
    case ROUTES.signedOut: return 'signed-out'
    case ROUTES.unavailable: return 'unavailable'
    default: return 'not-found'
  }
}
